<i18n>
{
  "fr": {
    "tool-title": "Piézomètre virtuel",
    "HEADS-label": "Niveau [msm]",
    "HEADS-unit": "msm",
    "EZNS-label": "Profondeur [m]",
    "EZNS-unit": "m",
    "no-data-title": "Aucune donnée",
    "HEADS": "Niveau piézométrique",
    "EZNS": "Épaisseur de la zone non saturée",
    "reference-title": "Valeurs de référence (1976-2017)",
    "catalog-title": "Catalogue mensuel"
  },
  "de": {

  }
}
</i18n>

<template>
  <div id="gw-heads-raster-point" class="tool" v-if="show">
    <h3>{{$t('tool-title')}}</h3>
    <md-content class="md-scrollbar main-container" v-if="!loading">
      <md-empty-state
        v-if="nodata"
        class="empty-data"
        md-rounded
        md-icon="graphic_eq"
        :md-label="$t('no-data-title')"
        :md-size="170"
      >
      </md-empty-state>
      <div class="graph-option" v-if="!nodata">
        <md-field class="option">
          <md-select v-model="selectedParameterType" :disabled="datasourceLoading">
            <md-option value="HEADS">{{ $t('HEADS') }}</md-option>
            <md-option value="EZNS">{{ $t('EZNS') }}</md-option>
          </md-select>
        </md-field>
      </div>
      <div class="data-graph" v-if="!nodata">
        <plotly
          v-if="showGraph"
          :data="graphData"
          :layout="graphLayout"
          :mouseOverValue="highlightedTimestamp"
          ref="plotly"
        />
      </div>
      <div class="datasources" v-if="!nodata">
        <div :class="['datasource', 'reference', {'disabled': !datasource.show}]" v-for="datasource in referenceDatasourcesToDisplay" :key="datasource.code" @click="toggleDatasource(datasource)">
          <div class="datasource-loading" v-if="datasource.loading">
            <md-progress-spinner :md-diameter="10" :md-stroke="2" md-mode="indeterminate"></md-progress-spinner>
          </div>
          <div class="datasource-linecolor" v-if="datasource.show && !datasource.loading" :style="{'background-color': datasource.color}"></div>
          <div class="datasource-title">{{ $t('reference-title') }}</div>
        </div>
        <div :class="['catalog', 'monthly']" v-for="catalog in monthlyDatasourcesToDisplay" :key="catalog.code">
          <div class="catalog-title">{{ $t('catalog-title') }}</div>
          <div class="datasource-year-container">
            <div :class="['datasource', 'year', {'disabled': !datasource.show || datasource.nodata}]" v-for="datasource in catalog.datasources" :key="datasource.code" @click="toggleDatasource(datasource)">
              <div class="datasource-loading" v-if="datasource.loading">
                <md-progress-spinner :md-diameter="10" :md-stroke="2" md-mode="indeterminate"></md-progress-spinner>
              </div>
              <div class="datasource-linecolor" v-if="datasource.show && !datasource.loading && !datasource.nodata" :style="{'background-color': datasource.color}"></div>
              <div class="datasource-title">{{ datasource.year }}</div>
            </div>
          </div>
        </div>
      </div>
    </md-content>
    <div class="loading" v-if="loading">
      <md-progress-spinner class="loading-animation" :md-diameter="40" :md-stroke="5" md-mode="indeterminate"></md-progress-spinner>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'

import MapStoreMixin from '@/mixins/store/map'
import LayerStoreMixin from '@/mixins/store/layer'
import TimeStoreMixin from '@/mixins/store/time'

import GwHeadsRefLayer from '@/components/rasters/gwHeadsRefLayer'
import Layer from '@/components/vue-map/layers/layer'
import plotly from '@/components/common/plotly'
import { PlotlyData, PlotlyXAxis, PlotlyYAxis, PlotlyLayout } from '@/utils/plotly'

import SourceProxy from '@/proxies/source'

const NODATA_VALUES = [-3.4028231e+38, -9999, 9999]

export default {
  name: 'GwHeadsRasterPoint',
  components: {
    plotly
  },
  mixins: [
    MapStoreMixin,
    LayerStoreMixin,
    TimeStoreMixin
  ],
  props: {
    params: {
      type: Object,
      default: () => { return {} }
    }
  },
  computed: {
    show () {
      return this.currentGeom !== null && this.currentGeom.type === 'Point'
    },
    nodata () {
      // On se base sur les rasters de références pour indiquer s'il y a des valeurs à afficher ou non
      return this.datasources.filter((datasource) => datasource.data !== null && !datasource.year && datasource.nodata).length > 0
    },
    showGraph () {
      return !this.loading && !_.isEmpty(this.graphData) && !this.nodata
    },
    datasourceLoading () {
      return this.datasources.filter((datasource) => datasource.loading).length > 0
    },
    graphLayout () {
      let toReturn = new PlotlyLayout()
      toReturn.xaxis = new PlotlyXAxis()
      toReturn.xaxis.tickvals = ['1','2','3','4','5','6','7','8','9', '10', '11', '12']
      toReturn.xaxis.ticktext = moment.localeData('fr').monthsShort()
      toReturn.yaxis = new PlotlyYAxis(this.$t(`${this.selectedParameterType}-label`))
      toReturn.yaxis.range = this.calculateRangeFromReference()
      toReturn.yaxis.fixedrange = true
      toReturn.margin = {t: 30, b: 40, r: 30}
      toReturn.displayHoverLine()

      toReturn.shapes.push(this.timeMarker)

      return toReturn
    },
    timeMarker () {
      return{
        class: 'timemarker',
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: parseInt(this.dateMiddle.format("M")),
        y0: 0,
        x1: parseInt(this.dateMiddle.format("M")),
        y1: 1,
        line: {
          color: 'red'
        }
      }
    },
    highlightedTimestamp () {
      if (this.needRefreshHoverValue) return null
      return this.dateMiddle.format("M")
    },
    graphData () {
      let toReturn = []
      this.datasources.forEach((datasource) => {
        if (datasource.data !== null && datasource.show) toReturn.push(datasource.data)
      })
      return toReturn
    },
    referenceDatasourcesToDisplay () {
      return this.datasources.filter((datasource) => !datasource.year && datasource.code.includes(this.selectedParameterType))
    },
    monthlyDatasourcesToDisplay () {
      // L'affichage doit représenter les sources de données temporelles en catalogue d'année à sélectionner
      let catalogs = _.groupBy(this.datasources.filter((datasource) => !!datasource.year && datasource.code.includes(this.selectedParameterType)), (datasource) => { return datasource.catalogCode })
      let toReturn = []
      for (const [key, value] of Object.entries(catalogs))  {
        toReturn.push({
          code: key,
          name: value[0].catalogName,
          datasources: value
        })
      }
      return toReturn
    },
    loadCatalog () {
      return !this.params.fetchRefOnly
    }
  },
  watch: {
    currentGeom () {
      this.loaded = false
      this.updateTool()
    },
    dateMiddle () {
      this.refreshGraph()
      if (this.dateMiddle.year() !== this.selectedYear) {
        this.selectedYear = this.dateMiddle.year()
      }
    },
    selectedYear () {
      if (!this.loadCatalog) return
      // On éteind toutes les couches temporelles allumées qui ne sont pas de l'année sélectionnée
      this.datasources.filter((datasource) => !!datasource.year && datasource.year !== this.selectedYear && datasource.show && datasource.code.includes(this.selectedParameterType)).forEach((datasource) => this.disableDatasource(datasource))
      // On allume les couches temporelles qui sont de l'année sélectionnée
      this.datasources.filter((datasource) => !!datasource.year && datasource.year === this.selectedYear && !datasource.show && datasource.code.includes(this.selectedParameterType)).forEach((datasource) => this.enableDatasource(datasource))
    },
    selectedParameterType () {
      // On eteind toutes les sources de données
      this.datasources.filter((datasource) => datasource.show).forEach((datasource) => this.disableDatasource(datasource))
      // On allume la source de donnée de référence
      this.enableDatasource(this.datasources.find((datasource) => datasource.code === `${this.selectedParameterType}_REF`))
      // On allume les couches temporelles qui sont de l'année sélectionnée
      if (this.loadCatalog) this.datasources.filter((datasource) => !!datasource.year && datasource.year === this.selectedYear && !datasource.show && datasource.code.includes(this.selectedParameterType)).forEach((datasource) => this.enableDatasource(datasource))
      this.refreshHoverValue()
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateTool()
    })
  },
  beforeDestroy() {
    this.removeTool()
    this.clearHoverFeature()
  },
  data() {
    return {
      loading: false,
      loaded: false,
      rasters: [],
      datasources: [],
      selectedYear: moment.utc().year(),
      selectedParameterType: 'HEADS',
      needRefreshHoverValue: false
    }
  },
  methods: {
    updateTool () {
      if (this.loading) return
      if (this.loaded) return this.loading = false
      if (this.currentGeom !== null && this.currentGeom.type === 'Point') this.load()
      else this.removeTool()
    },
    removeTool () {
      if (this.currentGeom !== null && this.currentGeom.type === 'Point') this.clearHoverFeature()
    },
    load () {
      this.loading = true
      this.loaded = false
      this.setHoverGeoJSON(this.currentGeom)
      this.setDefaultHoverStyle()
      this.rasters = []
      this.datasources = []
      this.selectedYear = moment.utc().year()
      this.setCurrentObject({})
      this.fetchRasters(this.params.sourcesToFetch).then(() => {
        this.generateDatasources().then(() => {
          this.loading = false
          this.loaded = true
          // On charge les sources de données de références
          this.toggleDatasource(this.datasources.find((datasource) => datasource.code === `${this.selectedParameterType}_REF`))
        }).catch(this.handleError)
      }).catch(this.handleError)
    },
    // Récupération des sources de données rasters et création de leur objet respectif
    fetchRasters (sourceIds) {
      return new Promise((resolve, reject) => {
        SourceProxy.getSources(sourceIds).then((sources) => {
          sources.forEach((source) => {
            if (source.geoserverConfig.sourceName.includes('REF')) this.rasters.push(new GwHeadsRefLayer(source))
            else this.rasters.push(Layer.fromSource(source))
          })
          resolve()
        }).catch(reject)
      })
    },
    generateDatasources () {
      return new Promise((resolve, reject) => {
        let raster = null
        let toolParams = null

        this.datasources = []
        // Sources de référence
        this.rasters.filter((raster) => raster.geoserverConfig.sourceName.includes('REF')).forEach((raster) => {
          toolParams = raster.webviewConfig.availableTools.find((tool) => tool.key === 'GW_HEADS_RASTER_POINT').params
          this.datasources.push({
            code: raster.geoserverConfig.sourceName,
            raster: raster,
            show: false,
            loading: false,
            data: null,
            nodata: false,
            fetch: this.fetchReferenceData.bind(this, raster),
            convertToPlotlyData: this.convertReferenceGraphData,
            color: toolParams.color
          })
        })

        // Catalogue mensuel (source temporelle)
        // Récupération de la plage de temp disponible sur le webservice
        let toFetch = []
        let monhtlyRasters = this.rasters.filter((raster) => raster.geoserverConfig.sourceName.includes('MONTHLY'))
        monhtlyRasters.filter((raster) => raster.geoserverConfig.sourceName.includes('MONTHLY')).forEach((raster) => {
          toFetch.push(raster.getTimeDimension())
        })

        Promise.all(toFetch).then((timeDimensions) => {
          let allYears = []
          let years = []
          let currentDate = null

          timeDimensions.forEach((timeDimension, index) => {
            raster = monhtlyRasters[index]
            toolParams = raster.webviewConfig.availableTools.find((tool) => tool.key === 'GW_HEADS_RASTER_POINT').params

            // Calculs des années disponible
            years = []
            currentDate = timeDimension.dateFrom.clone()
            while (timeDimension.dateTo >= currentDate) {
              years.push(currentDate.year())
              allYears.push(currentDate.year())
              currentDate.add(1, 'year')
            }

            // Génération d'une source de donnée par année
            years.forEach((year) => {
              this.datasources.push({
                code: `${raster.geoserverConfig.sourceName}_${year}`,
                catalogCode: raster.geoserverConfig.sourceName,
                catalogName: raster.metadata.name,
                color: toolParams.colorByYear.find((d) => d.year === year).color,
                year: year,
                raster: raster,
                show: false,
                loading: false,
                data: null,
                nodata: false,
                fetch: this.fetchTimeDependentData.bind(this, raster, year),
                convertToPlotlyData: this.convertTimeDependentGraphData
              })
            })
          })
          // Sélection de la première année à afficher
          this.selectedYear = _.min(allYears)
          resolve()
        }).catch(reject)
      })
    },
    // Récupération des valeurs sur les sources temporelles
    fetchTimeDependentData (raster, year) {
      return new Promise((resolve, reject) => {
        let dateFrom = moment.utc(`${year}-01-01`)
        let dateTo = moment.utc(`${year}-12-01`)

        raster.getValuesFromGeom(this.mapRef, this.currentGeom, dateFrom, dateTo).then(resolve).catch(reject)
      })
    },
    convertTimeDependentGraphData (datasource, data) {
      datasource.nodata = data.filter((d) => NODATA_VALUES.includes(d.value)).length === data.length
      let toReturn = new PlotlyData('', data.map((d) => (moment.utc(d.event).month() + 1).toString()), data.map((d) => this.dealRasterValue(d.value)), null, datasource.color)
      let unit = this.$t(`${this.selectedParameterType}-unit`)
      toReturn.hovertemplate = `%{y:.2f} <extra>[${unit}]</extra>`
      toReturn.showlegend = false
      toReturn.line.width = 2
      return toReturn
    },
    convertReferenceGraphData (datasource, data) {
      datasource.nodata = data.filter((d) => NODATA_VALUES.includes(d.value)).length === data.length
      let toReturn = new PlotlyData('', data.map((d) => d.month), data.map((d) => this.dealRasterValue(d.value)), null, datasource.color)
      let unit = this.$t(`${this.selectedParameterType}-unit`)
      toReturn.hovertemplate = `%{y:.2f} <extra>[${unit}]</extra>`
      toReturn.showlegend = false
      toReturn.line.width = 2
      return toReturn
    },
    dealRasterValue (value) {
      if (this.selectedParameterType === 'EZNS') return value * -1
      if (NODATA_VALUES.includes(value)) return null
      return value
    },
    // Récupération des valeurs sur les sources de références (gwHeadsRefLayer)
    fetchReferenceData (raster) {
      return raster.getValuesFromGeom(this.mapRef, this.currentGeom)
    },
    toggleDatasource (datasource) {
      if (datasource.show) return this.disableDatasource(datasource)
      this.enableDatasource(datasource)
    },
    enableDatasource (datasource) {
      // Si les données ont déjà été chargé, on change le state
      if (datasource.data !== null) {
        datasource.show = true
        this.refreshGraph()
        return
      }

      // Les données n'ont pas encore été chargées
      datasource.loading = true
      datasource.fetch().then((data) => {
        datasource.data = datasource.convertToPlotlyData(datasource, data)
        datasource.loading = false
        datasource.show = true

        // Mise à jour des datasources pour la réactivité de vue
        this.$set(this.datasources, this.datasources.indexOf(datasource), datasource)
        this.refreshGraph()
        this.refreshHoverValue()
      })
    },
    disableDatasource (datasource) {
        datasource.show = false
        this.refreshGraph()
        return
    },
    refreshGraph () {
      this.$nextTick(() => {
        if (typeof this.$refs.plotly !== 'undefined') {
          this.$refs.plotly.react()
          this.$refs.plotly.relayout()
        }
      })
    },
    calculateRangeFromReference () {
      // Calcule des bornes du graph sur l'axe y:
      // Prendre la première source de donnée de référence et faire +- un delta configuré dans les paramètres de la source sur la moyenne des données
      let referenceDatasource = _.head(this.referenceDatasourcesToDisplay)
      if (typeof referenceDatasource === 'undefined') return []
      let referenceRaster = referenceDatasource.raster
      if (referenceDatasource.data === null) return []

      let toolParams = referenceRaster.webviewConfig.availableTools.find((tool) => tool.key === 'GW_HEADS_RASTER_POINT').params
      let meanReference = _.mean(referenceDatasource.data.y)
      return [(meanReference - toolParams.deltaMeanRange), (meanReference + toolParams.deltaMeanRange)]
    },
    refreshHoverValue () {
      // Debounce du pauvre
      this.needRefreshHoverValue = true
      setTimeout(() => { this.needRefreshHoverValue = false }, 100)
    },
    handleError (e) {
      console.log(e)
    }
  }
}
</script>

<style lang="scss">
  #gw-heads-raster-point {
    .datasources {
      padding-left: 15px;
      padding-right: 15px;
    }

    .datasource {
      display: flex;
      align-items: center;

      .datasource-loading {
        margin-right: 15px;
      }

      &.disabled {
        opacity: 0.5;
      }

      .datasource-title {
        cursor: pointer;
        user-select: none;
        padding-right: 5px;
      }

      .datasource-linecolor {
        width: 15px;
        height: 2px;
        margin-right: 10px;
      }
    }

    .graph-option {
      display: flex;
      align-items: center;
      justify-content: center;

      .md-field {
        margin: 0px;
        width: 250px;
      }
    }

    .catalog {
      &.monthly {
        margin-top: 10px;
      }

      .datasource-year-container {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .catalog-title {
        margin-left: 25px;
      }

      .datasource.year {
        height: 30px;
        min-width: 55px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #d4d4d4;
        margin: 5px;
        cursor: pointer;
        margin-right: 5px;

        .datasource-loading {
          margin-right: 5px;
          margin-left: 10px;
        }

        &.disabled .datasource-title{
          margin-left: 5px;
        }

        .datasource-loading {
          margin-left: 10px;
        }

        .datasource-linecolor {
          margin-left: 5px;
        }
      }
    }

    .data-graph {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 240px;
      margin-bottom: 10px;

      & .plotly {
        height: 230px;
        width: 100%;
      }
    }

    .empty-data {
      margin-top: 15px;
      margin-bottom: 15px;

      .md-icon {
        width: 80px;
        min-width: 80px;
        font-size: 80px !important;
      }
      .md-empty-state-label {
        font-size: 18px;
        line-height: 18px;
      }

      &.md-empty-state-leave-active {
        display: none !important;
      }
    }
  }
</style>
